import {
  symbolData,
  COIN_JACKPOT_SYMBOL_ID,
  JACKPOT_NAME_TO_SKIN,
} from "../../../SymbolData";
import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Container, useApp } from "@pixi/react";
import { Settings } from "../../../settings";
import Reel from "../Reel/Reel";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import ReelSetMask from "../ReelSetMask/ReelSetMask";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import { warmRevealedCards } from "../GambleScatter/gambleScatterConfig";
import { warmGoldenCells } from "../Mystery/goldenMysteryConfig";
import {
  SYMBOL_SLIDE_1,
  SYMBOL_SLIDE_HNW,
} from "../../../Components/UserInterface/secondary/Menu/paytableConfig";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import FontGenerator from "../../../utils/FontGenerator";
import { useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectHoldAndWinActive,
  selectFreeSpinActive,
  selectAutoPlayActive,
} from "../../../State/selectors";
import { selectFreeSpinsActive } from "../../../State/Slices/freeSpinsSlice";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import { useReelControl } from "../../../Contexts/ReelControlContext";

// Module-level cache of the first round's reels: set once and never changed,
// so later rounds do not hand the Reels a new `reel` prop (each Reel takes
// round updates in its own effect).
let cachedReels = null;

/**
 * The 5 base reels, in native units (NATIVE_REELSET_SIZE; the spine's scale
 * does the resize). Owns the mask, the one shared sprite container all reels
 * paint into, the batched landing animations, and the composite textures'
 * density, which follows the on-screen cell (`screenSymbolHeight`).
 */
const ReelSet = memo(
  ({
    symbolWidth,
    symbolHeight,
    dividerDimensions,
    reelSpacing,
    screenSymbolHeight,
  }) => {
    const app = useApp();
    // ── The composites' density follows the cell ON SCREEN ──
    // symbolHeight is the native 214 and never changes; the spine's scale
    // carries the viewport. The density needs the on-screen height, or a big
    // screen magnifies 1x pictures and blurs them.
    const densityHeight = screenSymbolHeight ?? symbolHeight;
    const { reelRefs, reelSetMaskRef } = useReelRefs();
    const { triggerReelLandingAnimations } = useReelLandingAnimations();
    const { spacebarTurboActive } = useReelControl();
    const currentRound = useSelector(selectCurrentRound);
    const autoPlayActive = useSelector(selectAutoPlayActive);
    const maskRef = useRef(null);
    // ONE container for every reel's sprites: symbols sort only against
    // siblings, so per-reel containers would make cross-reel priority
    // impossible (the spine overlay is flat for the same reason).
    const sharedSymbolsContainerRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);
    const holdAndWinActive = useSelector(selectHoldAndWinActive);
    const freeSpinActive = useSelector(selectFreeSpinActive);
    // ── THIS game's bonus flag ──
    // `freeSpinActive` above is the legacy featureGame flag and is never
    // raised in this game. Without this one the base board (70 sprites) would
    // draw under the bonus room every frame.
    const freeSpinsActive = useSelector(selectFreeSpinsActive);

    // Cache the reels once (see cachedReels).
    if (!cachedReels && currentRound?.reels) {
      cachedReels = currentRound.reels;
    }

    // Use cached reels, sliced to configured REEL_COUNT (server may send more)
    const reels = cachedReels
      ? cachedReels.slice(0, Settings.REEL_COUNT)
      : null;

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        // Share mask with other components via context
        if (reelSetMaskRef) reelSetMaskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady, reelSetMaskRef],
    );

    const triggerLandingRef = useRef(triggerReelLandingAnimations);
    useEffect(() => {
      triggerLandingRef.current = triggerReelLandingAnimations;
    }, [triggerReelLandingAnimations]);

    // Batch landing animations - collect all reel data and trigger once when last reel stops
    // ONLY during turbo mode for optimal performance
    const pendingLandingAnimations = useRef(new Map()); // Map<reelIndex, reelData>
    const rafIdRef = useRef(null);

    const onReelStop = useCallback(
      (reelIndex, reelData) => {
        if (!reelData) return;

        // Check if turbo mode is active (spacebar turbo or autoplay)
        const isTurboMode = spacebarTurboActive || autoPlayActive;

        // TURBO MODE: Batch all landing animations until last reel stops
        if (isTurboMode) {
          // Store this reel's data
          pendingLandingAnimations.current.set(reelIndex, reelData);

          // If this is the last reel (index 4), trigger all landing animations at once
          if (reelIndex === Settings.REEL_COUNT - 1) {
            // Cancel any pending RAF
            if (rafIdRef.current !== null) {
              cancelAnimationFrame(rafIdRef.current);
            }

            // Collect all texture hide operations from all reels
            const allTextureHideOps = [];

            try {
              // Process all reels in order - show all spine animations first
              for (let i = 0; i < Settings.REEL_COUNT; i++) {
                const data = pendingLandingAnimations.current.get(i);
                if (data) {
                  // skipRAF=true to prevent individual RAF calls
                  const hideOps = triggerLandingRef.current?.(
                    i,
                    data,
                    null,
                    true,
                  );
                  if (hideOps && hideOps.length > 0) {
                    allTextureHideOps.push(...hideOps);
                  }
                }
              }
            } catch (err) {
              // Fail silently
            }

            // Single RAF to hide all textures at once (after all spine animations are set up)
            if (allTextureHideOps.length > 0) {
              rafIdRef.current = requestAnimationFrame(() => {
                allTextureHideOps.forEach(({ reelController, symbolIndex }) => {
                  if (reelController.hideSymbolTexture) {
                    reelController.hideSymbolTexture(symbolIndex);
                  }
                });

                // Clear pending animations
                pendingLandingAnimations.current.clear();
                rafIdRef.current = null;
              });
            } else {
              // No textures to hide, just clear
              pendingLandingAnimations.current.clear();
            }
          }
        } else {
          // REGULAR/MANUAL MODE: Trigger landing animations immediately per reel
          // This provides instant visual feedback for manual stops
          try {
            triggerLandingRef.current?.(reelIndex, reelData);
          } catch (err) {
            // Fail silently
          }
        }
      },
      [spacebarTurboActive, autoPlayActive],
    );

    // Initialize and consume spine-extracted textures + generated fonts
    const { symbolsSpriteSheet, symbolsSpriteSheetBlurred } = useMemo(() => {
      if (app?.renderer) {
        SpineTextureExtractor.initialize(app.renderer);
        SpineAnimationData.initialize();
        FontGenerator.initialize(app.renderer);
        // Before the two warms below: they capture at the density this sets,
        // and a set warmed at the wrong density is blurry.
        SpineTextureExtractor.setCompositeDensity(densityHeight);
        // Every settled gamble-reveal picture, made now rather than during the
        // reveal itself — 18 textures, the same set-and-pick the symbols above
        // already use. See warmRevealedCards.
        warmRevealedCards();
        // And every GOLDEN cell: the twelve coins, the four multipliers, the
        // magnet and both games' scatters. Built lazily they were built
        // mid-feature, several in one frame, stalling a wide board for a
        // second as the cells landed. See warmGoldenCells.
        warmGoldenCells();
        // Every jackpot coin's tier picture (ReelControllerV2, getSkinTexture),
        // so a tier's first landing does not build a spine and a RenderTexture
        // mid-spin. Same cache key the reels ask with.
        const jackpotSpine = symbolData[COIN_JACKPOT_SYMBOL_ID]?.spine;
        if (jackpotSpine) {
          for (const skin of Object.values(JACKPOT_NAME_TO_SKIN)) {
            SpineTextureExtractor.getSkinTexture(jackpotSpine, skin);
          }
        }
        // And every picture the PAYTABLE shows (2026-10-30): built on its first
        // open they crashed an iPhone 11 — now made here, behind the loading
        // curtain, and the paytable only reads them. See warmPaytableImages.
        SpineTextureExtractor.warmPaytableImages(
          [...SYMBOL_SLIDE_1, ...SYMBOL_SLIDE_HNW]
            .map((s) => s.symbolName)
            .filter(Boolean),
        );
      }
      return {
        symbolsSpriteSheet: SpineTextureExtractor.getSymbols(),
        symbolsSpriteSheetBlurred: SpineTextureExtractor.getBlurredSymbols(),
      };
      // densityHeight is deliberately NOT a dependency: this block also
      // builds the symbol sheet and the fonts, which cost far more and do not
      // care about the viewport. The effect below re-warms only what the
      // density changed.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app?.renderer]);

    // ── Re-warm the composites when the on-screen cell changes ──
    //
    // A composite's sharpness depends on the on-screen cell size; the density
    // carries that and is part of the cache key. Without this, the first
    // landing after a resize would re-capture a board's worth of cells on the
    // frame they land. Warmed here, where nothing is moving. No-op unless the
    // bucket moved (setCompositeDensity rounds to halves).
    useEffect(() => {
      if (!app?.renderer) return;
      if (!SpineTextureExtractor.setCompositeDensity(densityHeight)) return;
      warmRevealedCards();
      warmGoldenCells();
    }, [app?.renderer, densityHeight]);

    // Cache symbols data
    const reelSymbolsData = useMemo(() => {
      if (!symbolsSpriteSheet) return null;
      return (
        symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
      );
    }, [symbolsSpriteSheet]);

    // Memoize reel container Y position (constant for all reels)
    const reelContainerY = useMemo(
      () =>
        -symbolHeight * (1.5 * Settings.SYMBOLS_VISIBLE - 0.5) - symbolHeight,
      [symbolHeight],
    );

    // Memoize reel container X positions (one per reel)
    const reelPositions = useMemo(() => {
      const centerOffset = (Settings.REEL_COUNT - 1) / 2;
      return reels
        ? reels.map((_, index) => ({
            x: reelSpacing * (index - centerOffset),
            y: reelContainerY,
          }))
        : [];
    }, [reels, reelSpacing, reelContainerY]);

    // Guard: Don't render until reels and sprite textures are ready
    // Blurred textures are optional (controlled by SPIN.BLURRED_TEXTURES_ENABLED)
    if (
      !reels ||
      !Array.isArray(reels) ||
      reels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <>
        <ReelSetMask
          ref={setMaskRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
        />

        {maskReady && (
          <Container
            visible={!holdAndWinActive && !freeSpinActive && !freeSpinsActive}
            name="reelset"
            mask={maskRef.current}
            x={0}
            y={0}
            zIndex={1}
            sortableChildren
          >
            {/* Every reel's sprites, as siblings. Rendered first so its ref is
                attached before any Reel effect builds a controller against it. */}
            <Container
              ref={sharedSymbolsContainerRef}
              name="reel_symbols"
              x={0}
              y={reelContainerY}
              zIndex={1}
              sortableChildren
            />

            {reels.map((reel, index) => {
              const position = reelPositions[index];
              if (!position) return null;

              // Holds only the tension dim overlay; zIndex keeps the dim
              // above the symbols.
              return (
                <Container
                  key={index}
                  name={`reel_container_${index}`}
                  x={position.x}
                  y={position.y}
                  zIndex={100}
                >
                  <Reel
                    ref={(el) => (reelRefs.current[index] = el)}
                    reel={reel}
                    index={index}
                    symbolWidth={symbolWidth}
                    symbolHeight={symbolHeight}
                    symbolsSpriteSheet={symbolsSpriteSheet}
                    symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
                    reelSymbolsData={reelSymbolsData}
                    onReelStop={onReelStop}
                    sharedSymbolsContainerRef={sharedSymbolsContainerRef}
                    reelXOffset={position.x}
                  />
                </Container>
              );
            })}
          </Container>
        )}
      </>
    );
  },
);

export default ReelSet;
